import React from 'react';
import {
  bool, func, array, string, oneOfType,
} from 'prop-types';
import cn from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { DatePickerRange, Input, InputLabel } from '@picsio/ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import { TIME_ZONES } from '@picsio/db/src/constants';

import {
  datePickerPlaceholderWithTime, datePickerDateFormat, datePickerTimeFormat, isValidDate,
} from '../../../shared/dateLocale';
import localization from '../../../shared/strings';
import Logger from '../../../services/Logger';
import { Checkbox } from '../../../UIComponents';
import ua from '../../../ua';

import '../styles/restrict.scss';

dayjs.extend(utc);
dayjs.extend(tz);

const maxReasonLength = 450;

class Restrict extends React.Component {
  /** propTypes */
  static propTypes = {
    isVisible: bool,
    inProgress: bool,
    disabled: bool,
    selectedAssets: array,
    isRestricted: bool,
    reason: string,
    toggleVisibility: func,
    onChange: func,
    restrictStartAtPlaceholder: oneOfType([string, bool]),
    restrictExpiresAtPlaceholder: oneOfType([string, bool]),
    teamRestrictReason: string,
  };

  state = {
    initialReason: this.props.reason || null,
    reason: this.props.reason || null,
    reasonError: '',
  };

  isMobile = ua.browser.isNotDesktop() || ua.isMobileApp();

  static getDerivedStateFromProps(props, state) {
    if (props.reason !== state.initialReason) {
      return {
        reason: props.reason,
        initialReason: props.reason,
      };
    }

    return null;
  }

  handleChangeRestrictStatus = (value) => {
    const { reason } = this.state;
    const { expiresAt, startAt, teamRestrictReason } = this.props;
    const data = {
      isRestricted: value,
    };

    if (value) {
      if (
        reason !== localization.DETAILS.placeholderMultipleSelection
        && reason !== teamRestrictReason
        && reason !== localization.RESTRICT.RESTRICTED_REASON
      ) {
        data.reason = this.state.reason;
      }
      if (startAt) data.startAt = startAt;
      if (expiresAt) data.expiresAt = expiresAt;
    }

    Logger.log('User', 'RestrictChangeStatus', { assetIds: this.props.selectedAssets, data });
    this.props.onChange(this.props.selectedAssets, data);
  };

  handleTitleClick = () => this.props.toggleVisibility('detailsAssetRestrictVisibility');

  handleChangeReason = (event) => {
    const { value } = event.target;

    if (value.trim().length > maxReasonLength) {
      this.setState({ reasonError: localization.CUSTOMFIELDSSCHEMA.textErrorLengthLimit(maxReasonLength) });
    } else {
      if (this.state.reasonError) {
        this.setState({ reasonError: '' });
      }
      this.setState({ reason: value });
    }
  };

  handleBlurReason = (event) => {
    const { value } = event.target;
    if (value === this.state.initialReason) return;
    if (value.trim().length <= maxReasonLength) {
      Logger.log('User', 'RestrictChangeReason', { assetIds: this.props.selectedAssets });
      this.props.onChange(this.props.selectedAssets, {
        reason: value || null,
      });
    }
  };

  handleChangeDates = (key, value) => {
    const keyName = key === 'startAt' ? 'StartAt' : 'ExpiresAt';
    const data = {
      [key]: value,
    };

    if (value) {
      data.isRestricted = true;
    }

    Logger.log('User', `RestrictChange${keyName}`, { assetIds: this.props.selectedAssets, value });

    this.props.onChange(this.props.selectedAssets, data);
  };

  handleOnInputChangeStart = (date) => {
    let newDate = date;

    if (isValidDate(date)) {
      const tzoffset = date.getTimezoneOffset() * 60000;
      newDate = (new Date(date.getTime() - tzoffset)).toISOString().slice(0, -1);
    }
    const data = {
      startAt: newDate,
    };

    if (newDate) {
      data.isRestricted = true;
    }
    Logger.log('User', 'RestrictChangeStartAt', { assetIds: this.props.selectedAssets, newDate });

    this.props.onChange(this.props.selectedAssets, data);
  }

  handleOnInputChangeEnd = (date) => {
    let newDate = date;
    if (isValidDate(date)) {
      const tzoffset = date.getTimezoneOffset() * 60000;
      newDate = (new Date(date.getTime() - tzoffset)).toISOString().slice(0, -1);
    }
    const data = {
      expiresAt: newDate,
    };

    if (newDate) {
      data.isRestricted = true;
    }
    Logger.log('User', 'RestrictChangeExpiresAt', { assetIds: this.props.selectedAssets, newDate });

    this.props.onChange(this.props.selectedAssets, data);
  }

  parseOffsetToHours = (offsetStr) => {
    const m = offsetStr.match(/^([+-]?)(\d{1,2}):(\d{2})$/);
    if (!m) {
      throw new Error(`Invalid offset format: "${offsetStr}"`);
    }
    const [, sign, hh, mm] = m;
    const hours = parseInt(hh, 10);
    const minutes = parseInt(mm, 10);
    const value = hours + minutes / 60;
    return sign === '-' ? -value : value;
  }

  renderDateFields = () => {
    const {
      startAt: rawStartAt,
      expiresAt: rawExpiresAt,
      restrictStartAtPlaceholder,
      restrictExpiresAtPlaceholder,
      disabled,
      user,
    } = this.props;

    const userTz = user?.settings?.timeZone?.userTimeZone;
    const timezone = TIME_ZONES.find((tz) => tz.value === userTz);

    const toZonedDate = (isoString) => {
      if (!isoString || !dayjs(isoString).isValid()) return null;
      // interpret the string in user’s zone, then to JS Date

      const offset = this.parseOffsetToHours(timezone.utc);
      return dayjs.utc(isoString)
        .add(-offset, 'hour')
        .utc()
        .toISOString();
    };
    const formatForMobile = (isoString) => (isoString && dayjs(isoString).isValid()
      ? dayjs(isoString).format('YYYY-MM-DD')
      : '');

    // Compute day‐bounds once
    const today = dayjs();
    const startOfDay = today.startOf('day').toDate();
    const endOfDay = today.endOf('day').toDate();

    let startAt; let expiresAt; let startMaxTime; let expiresMinTime;

    if (this.isMobile) {
      startAt = formatForMobile(rawStartAt);
      expiresAt = formatForMobile(rawExpiresAt);
    } else {
      startAt = toZonedDate(rawStartAt);
      expiresAt = toZonedDate(rawExpiresAt);
      const sameDay = (a, b) => a && b && dayjs(a).isSame(b, 'day');

      // if both fall on the same day, enforce time bounds:
      startMaxTime = sameDay(startAt, expiresAt)
        ? expiresAt
        : dayjs().endOf('day').toDate();

      expiresMinTime = sameDay(startAt, expiresAt)
        ? startAt
        : dayjs().startOf('day').toDate();
    }

    return (
      <div className="UIInput">
        <DatePickerRange
          startDate={startAt ?? ''}
          endDate={expiresAt ?? ''}
          onInputChangeStart={this.handleOnInputChangeStart}
          onInputChangeEnd={this.handleOnInputChangeEnd}
          datePickerDateFormat={`${datePickerDateFormat} ${datePickerTimeFormat}`}
          datePickerTimeFormat={datePickerTimeFormat}
          popperClassName="datepickerFixed"
          popperPlacement="bottom-start"
          showTimeSelect
          popperModifiers={{
            offset: {
              enabled: true,
              offset: '5px, 10px',
            },
            preventOverflow: {
              enabled: true,
              escapeWithReference: false,
              boundariesElement: 'scrollParent',
            },
          }}
          datePickerOrder="column"
          labelStartDate="Start date"
          labelEndDate="Expires at"
          timeCaption="time"
          placeholderText={restrictStartAtPlaceholder || datePickerPlaceholderWithTime}
          endDateMinTime={expiresMinTime}
          endDateMaxTime={endOfDay}
          startDateMinTime={startOfDay}
          startDateMaxTime={startMaxTime}
          startDatePlaceholderText={restrictStartAtPlaceholder || datePickerPlaceholderWithTime}
          endDatePlaceholderText={restrictExpiresAtPlaceholder || datePickerPlaceholderWithTime}
          disabled={disabled}
        />
      </div>
    );
  };

  render() {
    const { props, state } = this;

    return (
      <div
        data-qa="details-component-restrict"
        className={cn('detailsPanel__item restrict', { act: props.isVisible, disabled: props.disabled })}
      >
        <div className="detailsPanel__title">
          <span className={cn('detailsPanel__title_text')} onClick={this.handleTitleClick}>
            {localization.DETAILS.textRestrict}
          </span>
          <div className="detailsPanel__title_buttons">
            {!props.disabled && (
              <Checkbox
                slide
                inProgress={props.inProgress}
                value={props.isRestricted}
                onChange={this.handleChangeRestrictStatus}
              />
            )}
          </div>
        </div>
        <CSSTransition in={props.isVisible} timeout={300} classNames="fade">
          <>
            <If condition={props.isVisible}>
              <div className="restrictSettings">
                <InputLabel>
                  {localization.RESTRICT.restrictReasonLabel}
                </InputLabel>
                <Input
                  component="textarea"
                  autoResize
                  minHeight={68}
                  maxHeight={148}
                  value={state.reason ?? ''}
                  onChange={this.handleChangeReason}
                  onBlur={this.handleBlurReason}
                  disabled={props.disabled}
                  error={!!state.reasonError}
                />
                <If condition={!!state.reasonError}>
                  <p className="PicsioInputHelperText PicsioInputHelperText--error">
                    {state.reasonError}
                  </p>
                </If>
                {this.renderDateFields()}
              </div>
            </If>
          </>
        </CSSTransition>
      </div>
    );
  }
}
export default Restrict;
